import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel } from '@/models/core.model';
import { PointAccountAdjustRequest } from '@/models/point-account.model';
import { adjustPointAccount } from '@/services/point-account.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<PointAccountAdjustRequest>({
  amount: Schema.Types.NumberType()
    .isRequired('This field is required.')
    .addRule(value => Number(value) !== 0, 'Amount must not be zero.'),
  note: Schema.Types.StringType().isRequired('A note is required for adjustments.'),
});

const emptyModel: PointAccountAdjustRequest = { amount: undefined, note: undefined };

interface AdjustDrawerProps extends DrawerProps {
  pointAccountId: number;
  onSubmitted?: () => void;
}

const AdjustDrawer = (props: AdjustDrawerProps) => {
  const { open, onClose, onSubmitted, pointAccountId, ...rest } = props;
  const [formValue, setFormValue] = useState<PointAccountAdjustRequest>({ ...emptyModel });
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (open) setFormValue({ ...emptyModel });
  }, [open]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setErrorModel(undefined);
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await adjustPointAccount(pointAccountId, { amount: Number(formValue.amount), note: formValue.note });
      if (result.success) {
        toastSuccessMessage('Account adjusted');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>Adjust Account Balance</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">Submit</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form name="adjust-point-account-form" fluid ref={formRef}
          onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
          formValue={formValue} model={modelSchema} disabled={loading}
        >
          <Form.Group>
            <Form.Label>Amount</Form.Label>
            <Form.Control name="amount" accepter={NumberInput} />
            <Form.HelpText>Positive adds to the balance, negative removes from it.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control name="note" />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default AdjustDrawer;
